/*
 * Arachne Audio — Headphone Cable Finder (HCF)
 * Vanilla JS — zero dependencies
 *
 * Typeahead = local fuzzy index (brands + models from metafields, which the
 * Shopify Predictive Search API cannot see) merged with live product results
 * from /search/suggest.json.
 *
 * data-* selectors used in the HTML:
 *   data-hcf-search        — search <input>
 *   data-hcf-clear         — clear <button>
 *   data-hcf-typeahead     — suggestions container
 *   data-hcf-modal-open    — "Browse all brands" trigger
 *   data-hcf-modal         — modal root
 *   data-hcf-modal-close   — modal close button
 *   data-hcf-brands-json   — JSON island: [{name, handle}]
 *   data-hcf-models-json   — JSON island: [{m, b, h}] (global pages only),
 *                            fetched from data-models-url (sections/hcf-model-index.liquid)
 *
 * Loaded by sections/ss-collection-navigation.liquid (one async tag per copy
 * of the section); everything it needs comes from data attributes and JSON
 * islands on each .hcf root, so the file holds no Liquid.
 *
 * Shopify section lifecycle:
 *   shopify:section:load   → mount()
 *   shopify:section:unload → unmount()
 */

(function () {
  'use strict';

  /* -------------------------------------------------------------------------
     Utility helpers
     ---------------------------------------------------------------------- */

  /**
   * Escape a string for safe use as a RegExp literal.
   * @param {string} str
   */
  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Escape HTML special characters before injecting text via innerHTML.
   * @param {string} str
   */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Wrap matched substrings in <mark> for typeahead highlighting.
   * @param {string} text   Full candidate string (already HTML-escaped)
   * @param {string} query  Raw query string
   */
  function highlight(text, query) {
    if (!query) return text;
    const re = new RegExp(`(${escapeRegExp(query)})`, 'gi');
    return text.replace(re, '<mark>$1</mark>');
  }

  /**
   * Normalise a string to its "compact" comparable form:
   * lowercase, all non-alphanumerics stripped.
   * "TH1000RP mk2", "th1000rp-mk2" and "TH1000RPmk2" all → "th1000rpmk2".
   * This is the backbone of typo/spacing tolerance across the whole finder.
   * @param {string} s
   */
  function compact(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /**
   * Levenshtein edit distance with early bail-out (for small strings only).
   * @param {string} a
   * @param {string} b
   */
  function editDistance(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 99;
    const m = a.length, n = b.length;
    if (!m) return n;
    if (!n) return m;
    let prev = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
      const cur = [i];
      for (let j = 1; j <= n; j++) {
        cur[j] = Math.min(
          prev[j] + 1,
          cur[j - 1] + 1,
          prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
      prev = cur;
    }
    return prev[n];
  }

  /**
   * Parse a JSON island <script> inside the section. Returns fallback on error.
   * @param {HTMLElement} section
   * @param {string} selector
   * @param {*} fallback
   */
  function parseIsland(section, selector, fallback) {
    const el = section.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[HCF] Bad JSON island', selector, e);
      return fallback;
    }
  }

  /*
   * Global model index (non-brand pages). Built by sections/hcf-model-index.liquid
   * and fetched through the Section Rendering API instead of being inlined, so
   * its loop over 47 brand collections no longer delays the page's first byte.
   * Cached per tab in sessionStorage, keyed by URL (locale) + country + currency.
   */
  function modelCacheKey(url) {
    const S = window.Shopify || {};
    return 'hcf_models_v1|' + url + '|' + (S.country || '') + '|' +
      ((S.currency && S.currency.active) || '');
  }

  function readModelCache(url) {
    try {
      const list = JSON.parse(window.sessionStorage.getItem(modelCacheKey(url)) || 'null');
      return Array.isArray(list) && list.length ? list : null;
    } catch (e) {
      return null; /* storage disabled / bad entry */
    }
  }

  function writeModelCache(url, list) {
    try {
      window.sessionStorage.setItem(modelCacheKey(url), JSON.stringify(list));
    } catch (e) { /* quota / storage disabled */ }
  }

  function fetchModelIndex(url) {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 10000) : null;
    return fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('[data-hcf-models-json]');
        const list = el ? JSON.parse(el.textContent) : null;
        if (!Array.isArray(list)) throw new Error('model index missing');
        return list;
      })
      .finally(() => clearTimeout(timer));
  }

  /**
   * Trap keyboard focus within `container` while a modal is open.
   * Returns a cleanup function that removes the keydown listener.
   * @param {HTMLElement} container
   * @returns {() => void}
   */
  function trapFocus(container) {
    const focusable = /** @type {NodeListOf<HTMLElement>} */ (
      container.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (!focusable.length) return () => {};

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();
    return () => container.removeEventListener('keydown', onKeydown);
  }

  /* -------------------------------------------------------------------------
     HeadphoneCableFinder — one instance per section
     ---------------------------------------------------------------------- */

  class HeadphoneCableFinder {
    /**
     * @param {HTMLElement} section  The .hcf root element
     */
    constructor(section) {
      this.section = section;

      this.contactUrl     = section.dataset.contactUrl || '/pages/contact-us';
      this.collectionsUrl = section.dataset.collectionsUrl || '/collections';

      /* --- DOM references -------------------------------------------------- */
      this.searchInput  = section.querySelector('[data-hcf-search]');
      this.searchClear  = section.querySelector('[data-hcf-clear]');
      this.typeahead    = section.querySelector('[data-hcf-typeahead]');
      this.chips        = section.querySelectorAll('.hcf__chip');
      this.allBrandsBtn = section.querySelector('[data-hcf-modal-open]');

      /*
       * The modal is a sibling of .hcf (placed outside for clean z-index), so
       * we look it up via the aria-controls attribute, falling back to the
       * nearest modal in the document if needed.
       */
      const modalId    = this.allBrandsBtn && this.allBrandsBtn.getAttribute('aria-controls');
      this.modal       = (modalId && document.getElementById(modalId)) ||
                         document.querySelector('[data-hcf-modal]');
      this.modalClose  = this.modal && this.modal.querySelector('[data-hcf-modal-close]');
      this.modalInner  = this.modal && this.modal.querySelector('.hcf__modal__inner');

      /* Local search data (JSON islands) */
      this.brands     = parseIsland(section, '[data-hcf-brands-json]', []);
      /* Translated typeahead labels (already HTML-escaped by | t: insert as HTML, not via escapeHtml) */
      this.str        = Object.assign({
        brands: 'Brands', viewAllCables: 'View all cables', models: 'Models', showCables: 'Show cables',
        cables: 'Cables', recent: 'Recent', getInTouch: 'get in touch',
        noResults: 'Nothing in our list yet. If your headphones aren&#39;t listed, we can almost certainly still make the cable:'
      }, parseIsland(section, '[data-hcf-i18n]', {}));
      this.modelIndex = parseIsland(section, '[data-hcf-models-json]', []);
      this.modelsUrl  = section.dataset.modelsUrl || '';
      /* idle -> loading -> ready | failed; 'ready' at once when nothing to fetch */
      this._modelsState   = (this.modelIndex.length || !this.modelsUrl) ? 'ready' : 'idle';
      this._modelsPromise = null;
      this._lastRemote    = null; /* last predictive-search result: {q, products} */

      /* State */
      this._activeIndex  = -1;   /* keyboard-highlighted typeahead row */
      this._trapCleanup  = null; /* focus trap teardown reference */
      this._prevFocus    = null; /* element focused before modal opened */
      this._debounceTimer   = null; /* typeahead debounce handle */
      this._abortController = null; /* cancels in-flight fetch */
      this._fetchToken      = 0;    /* discards stale fetch responses */
      this._lastLocal       = { brands: [], models: [] };

      /* Pre-bound event handlers (stored for clean removeEventListener) */
      this._onInput        = this._handleInput.bind(this);
      this._onKeydown      = this._handleKeydown.bind(this);
      this._onClear        = this._handleClear.bind(this);
      this._onDocClick     = this._handleDocumentClick.bind(this);
      this._onModalKey     = this._handleModalKeydown.bind(this);
      this._onOverlayClick = this._handleOverlayClick.bind(this);

      this._init();
    }

    /* -----------------------------------------------------------------------
       Init & teardown
       -------------------------------------------------------------------- */

    _init() {
      if (!this.searchInput) return; /* guard: section not ready */

      /* Brand-page mode: scope typeahead to this brand only */
      const currentBrand = this.section.dataset.brand || '';
      this.isBrandPage = currentBrand !== '';
      this.currentBrand = currentBrand;

      /* Search input */
      this.searchInput.addEventListener('input', this._onInput);
      this.searchInput.addEventListener('keydown', this._onKeydown);
      this.searchInput.addEventListener('focus', () => {
        this._loadModelIndex(); /* no-op once loaded or loading */
        /* Re-open typeahead on re-focus if there is already a query,
           otherwise offer the visitor's recent searches */
        if (this.searchInput.value.trim()) this._showTypeahead();
        else this._renderRecent();
      });

      /* Clear button */
      if (this.searchClear) {
        this.searchClear.addEventListener('click', this._onClear);
      }

      /* Close typeahead on any outside click */
      document.addEventListener('click', this._onDocClick);

      /* Brand chips */
      this.chips.forEach(chip => {
        chip.addEventListener('click', this._handleChipClick.bind(this));
      });

      /* Model-chip product grid filter (brand pages only) */
      if (this.isBrandPage) {
        this._initModelFilter();
        /* On brand pages, build the local model index from the product map */
        this._buildBrandPageModelIndex();
      }

      /* Non-brand pages: fetch the global model index in the background */
      if (!this.isBrandPage && this._modelsState === 'idle') this._scheduleModelIndex();

      /* "Browse all brands" button */
      if (this.allBrandsBtn) {
        this.allBrandsBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this._openModal();
        });
      }

      /* Modal close button */
      if (this.modalClose) {
        this.modalClose.addEventListener('click', () => this._closeModal());
      }

      /* Overlay click-outside-to-close */
      if (this.modal) {
        this.modal.addEventListener('click', this._onOverlayClick);
      }
    }

    /** Remove all event listeners — called on section:unload. */
    destroy() {
      if (this.searchInput) {
        this.searchInput.removeEventListener('input', this._onInput);
        this.searchInput.removeEventListener('keydown', this._onKeydown);
      }
      if (this.searchClear) {
        this.searchClear.removeEventListener('click', this._onClear);
      }
      document.removeEventListener('click', this._onDocClick);
      if (this.modal) {
        this.modal.removeEventListener('click', this._onOverlayClick);
        document.removeEventListener('keydown', this._onModalKey);
      }
      if (this._trapCleanup) {
        this._trapCleanup();
        this._trapCleanup = null;
      }
    }

    /* -----------------------------------------------------------------------
       Local fuzzy matching (brands + models)
       -------------------------------------------------------------------- */

    /**
     * Fuzzy-match the query against brand names.
     * Handles prefixes, substrings and typos ("senheiser" → Sennheiser).
     * @param {string} q
     * @returns {Array<{name: string, handle: string}>}
     */
    _matchBrands(q) {
      const cq = compact(q);
      if (cq.length < 2) return [];
      const scored = [];
      for (const b of this.brands) {
        /* Never suggest the brand page we are already on */
        if (this.isBrandPage && b.name === this.currentBrand) continue;
        const cb = compact(b.name);
        let score = -1;
        if (cb === cq) score = 0;
        else if (cb.startsWith(cq) || cq.startsWith(cb)) score = 1;
        else if (cq.length >= 3 && cb.includes(cq)) score = 2;
        else if (cq.length >= 4) {
          const d = editDistance(cq, cb);
          const tol = cq.length <= 5 ? 1 : 2;
          if (d <= tol) score = 2 + d;
        }
        if (score >= 0) scored.push({ b, score });
      }
      scored.sort((x, y) => x.score - y.score);
      return scored.slice(0, 2).map(x => x.b);
    }

    /**
     * Fuzzy-match the query against the model index.
     * Understands "brand + model" queries ("sennheiser hd650") and tolerates
     * missing spaces/hyphens plus 1–2 character typos.
     * @param {string} q
     * @returns {Array<{m: string, b: string, h: string}>}
     */
    _matchModels(q) {
      const cqFull = compact(q);
      if (cqFull.length < 2) return [];
      const out = [];
      for (const e of this.modelIndex) {
        const cm = compact(e.m);
        if (!cm) continue;
        const cb = compact(e.b);
        /* Strip a leading brand name so "akg k271" matches model "K271" */
        let rest = cqFull;
        if (cb && cqFull.startsWith(cb) && cqFull.length > cb.length) {
          rest = cqFull.slice(cb.length);
        }
        let score = -1;
        if (cm === rest) score = 0;
        else if (rest.length >= 2 && cm.startsWith(rest)) score = 1;
        else if (rest.length >= 3 && cm.includes(rest)) score = 2;
        else if (rest.length >= 4) {
          const d = editDistance(rest, cm);
          const tol = rest.length <= 5 ? 1 : 2;
          if (d <= tol) score = 3 + d;
        }
        if (score >= 0) out.push({ e, score });
      }
      out.sort((a, b) => a.score - b.score || a.e.m.length - b.e.m.length);
      return out.slice(0, 6).map(x => x.e);
    }

    /** On brand pages, derive the model index from the productModels map. */
    _buildBrandPageModelIndex() {
      if (!this.productModels) {
        /* _initModelFilter may have bailed; parse the map defensively */
        try {
          this.productModels = JSON.parse(this.section.dataset.productModels || '{}');
        } catch (e) {
          this.productModels = {};
        }
      }
      const seen = new Set();
      const index = [];
      for (const models of Object.values(this.productModels)) {
        for (const m of models) {
          const key = compact(m);
          if (!key || seen.has(key)) continue;
          seen.add(key);
          index.push({ m: String(m).trim(), b: this.currentBrand, h: '', local: true });
        }
      }
      this.modelIndex = index;
    }

    /** Use the session cache, else fetch after window load when the browser is idle. */
    _scheduleModelIndex() {
      const cached = readModelCache(this.modelsUrl);
      if (cached) {
        this.modelIndex = cached;
        this._modelsState = 'ready';
        return;
      }
      const start = () => this._loadModelIndex();
      const whenIdle = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 3000 });
        else setTimeout(start, 1500);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }

    /** Fetch the index once. On failure the Brands and Cables groups keep working. */
    _loadModelIndex() {
      if (this._modelsState !== 'idle') return this._modelsPromise || Promise.resolve();
      this._modelsState = 'loading';
      this._modelsPromise = fetchModelIndex(this.modelsUrl)
        .then(list => {
          this.modelIndex = list;
          this._modelsState = 'ready';
          writeModelCache(this.modelsUrl, list);
        })
        .catch(() => { this._modelsState = 'failed'; })
        .then(() => this._refreshAfterModels(false));
      return this._modelsPromise;
    }

    /** Re-run local matching for the current query once the index has arrived. */
    _refreshAfterModels(force) {
      if (!this.searchInput || !this.typeahead) return;
      if (!force && !this.typeahead.classList.contains('is-open')) return;
      const q = this.searchInput.value.trim();
      if (q.length < 2) return;
      this._lastLocal = { brands: this._matchBrands(q), models: this._matchModels(q) };
      const remote = (this._lastRemote && this._lastRemote.q === q) ? this._lastRemote.products : null;
      this._renderResults(q, this._lastLocal.brands, this._lastLocal.models, remote);
      if (this.typeahead.querySelector('.hcf__typeahead-item, .hcf__no-results-item')) this._showTypeahead();
    }

    /* -----------------------------------------------------------------------
       Search / Typeahead
       -------------------------------------------------------------------- */

    _handleInput(e) {
      const q = (e.target.value || this.searchInput.value).trim();
      this._loadModelIndex(); /* in case input arrives without a focus event */

      this._syncClearBtn(q.length > 0);

      if (q.length < 2) {
        this._cancelSuggestions();
        this._closeTypeahead();
        return;
      }

      /* Local matches render instantly — remote products merge in after */
      this._lastLocal = {
        brands: this._matchBrands(q),
        models: this._matchModels(q)
      };
      this._renderResults(q, this._lastLocal.brands, this._lastLocal.models, null);
      this._showTypeahead();

      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this._fetchSuggestions(q), 200);
    }

    _fetchSuggestions(q) {
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();
      const token = ++this._fetchToken;

      /* Locale-aware root (e.g. /fr/) so suggestions work on all markets */
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      /* NOTE: predictive search caps resources[limit] at 10 — values above
         that can make the API reject the whole request. */
      const buildUrl = (query) =>
        `${root}search/suggest.json?q=${encodeURIComponent(query)}` +
        `&resources[type]=product&resources[limit]=10` +
        `&resources[options][fields]=title,tag,variants.title,vendor` +
        `&resources[options][unavailable_products]=hide`;

      /* If the compact query has letter→digit boundaries, prepare a spaced
         variant ("hd650" → "hd 650") — predictive search is prefix-per-word. */
      const spaced = q.replace(/([a-zA-Z])(\d)/g, '$1 $2');
      const urls = [buildUrl(q)];
      if (spaced !== q) urls.push(buildUrl(spaced));

      Promise.all(
        urls.map(u =>
          fetch(u, { signal: this._abortController.signal })
            .then(r => (r.ok ? r.json() : null))
            .catch(err => (err.name === 'AbortError' ? Promise.reject(err) : null))
        )
      )
        .then(datasets => {
          if (token !== this._fetchToken) return; /* stale */
          let products = [];
          for (const data of datasets) {
            const list = (data && data.resources && data.resources.results &&
                          data.resources.results.products) || [];
            products = products.concat(list);
          }

          /* Keep headphone cables, but be forgiving: missing tag data or a
             'cable' mention in the title also passes. */
          products = products.filter(p => {
            const tags = Array.isArray(p.tags)
              ? p.tags
              : (typeof p.tags === 'string' ? p.tags.split(',').map(t => t.trim()) : null);
            if (!tags || !tags.length) return true;
            if (tags.some(t => String(t).toLowerCase() === 'cable')) return true;
            return (p.title || '').toLowerCase().includes('cable');
          });

          /* Brand-page: keep products that mention the brand's first word OR
             exist in this collection's product map. (The old strict
             title.startsWith(brand) dropped everything on e.g. Verum Audio,
             whose products are titled "Verum 1 …".) */
          if (this.isBrandPage && this.currentBrand) {
            const firstWord = this.currentBrand.split(' ')[0].toLowerCase();
            products = products.filter(p => {
              if ((p.title || '').toLowerCase().includes(firstWord)) return true;
              const m = (p.url || '').match(/\/products\/([^/?#]+)/);
              return !!(m && this.productModels && this.productModels[m[1]]);
            });
          }

          /* Deduplicate by stripped title (same headphone, multiple series) */
          const seen = new Set();
          const unique = [];
          for (const p of products) {
            const stripped = (p.title || '')
              .replace(/ - (Aranea|Medusa|Cordura) Series$/i, '')
              .trim();
            const key = compact(stripped);
            if (!seen.has(key)) {
              seen.add(key);
              unique.push({ ...p, displayTitle: stripped });
            }
          }

          this._lastRemote = { q: q, products: unique.slice(0, 6) };
          this._renderResults(q, this._lastLocal.brands, this._lastLocal.models, this._lastRemote.products);
          this._showTypeahead();
        })
        .catch(err => {
          if (err && err.name === 'AbortError') return;
          /* Network failure: keep whatever local results are shown */
        });
    }

    /**
     * Render the merged suggestion list.
     * @param {string} query
     * @param {Array}  brands    matched brands
     * @param {Array}  models    matched models
     * @param {Array|null} products  remote products (null = still loading)
     */
    _renderResults(query, brands, models, products) {
      const rows = [];

      if (brands.length) {
        rows.push(`<div class="hcf__typeahead-group" aria-hidden="true">${this.str.brands}</div>`);
        for (const b of brands) {
          rows.push(`
            <div
              class="hcf__typeahead-item"
              role="option"
              aria-selected="false"
              data-url="${escapeHtml(this.collectionsUrl)}/${escapeHtml(b.handle)}"
              data-label="${escapeHtml(b.name)}"
              tabindex="-1"
            >
              <span>${highlight(escapeHtml(b.name), query)}</span>
              <span class="hcf__typeahead-item__sub">${this.str.viewAllCables}</span>
            </div>
          `);
        }
      }

      if (models.length) {
        rows.push(`<div class="hcf__typeahead-group" aria-hidden="true">${this.str.models}</div>`);
        for (const e of models) {
          if (e.local) {
            /* Brand page: activate the matching filter chip instead of navigating */
            rows.push(`
              <div
                class="hcf__typeahead-item"
                role="option"
                aria-selected="false"
                data-local-model="${escapeHtml(e.m)}"
                data-label="${escapeHtml(e.m)}"
                tabindex="-1"
              >
                <span>${highlight(escapeHtml(e.m), query)}</span>
                <span class="hcf__typeahead-item__sub">${this.str.showCables}</span>
              </div>
            `);
          } else {
            const url = `${this.collectionsUrl}/${e.h}?model=${encodeURIComponent(e.m)}`;
            rows.push(`
              <div
                class="hcf__typeahead-item"
                role="option"
                aria-selected="false"
                data-url="${escapeHtml(url)}"
                data-label="${escapeHtml(e.m)}"
                tabindex="-1"
              >
                <span>${highlight(escapeHtml(e.m), query)}</span>
                <span class="hcf__typeahead-item__sub">${escapeHtml(e.b)}</span>
              </div>
            `);
          }
        }
      }

      if (products && products.length) {
        rows.push(`<div class="hcf__typeahead-group" aria-hidden="true">${this.str.cables}</div>`);
        products.forEach(item => {
          rows.push(`
            <div
              class="hcf__typeahead-item"
              role="option"
              aria-selected="false"
              data-url="${encodeURI(item.url)}"
              data-label="${escapeHtml(item.displayTitle)}"
              tabindex="-1"
            >
              <span>${highlight(escapeHtml(item.displayTitle), query)}</span>
            </div>
          `);
        });
      }

      if (!rows.length) {
        /* Model index still loading: wait for it before showing (and reporting) a
           miss, and drop the previous query's rows so Enter cannot pick them */
        if (this._modelsState === 'loading') {
          this.searchInput.removeAttribute('aria-activedescendant');
          this.typeahead.innerHTML = '';
          this._activeIndex = -1;
          return;
        }
        if (products === null) return; /* nothing local yet, remote pending */
        this._renderNoResults(this.contactUrl, query);
        return;
      }

      this.typeahead.innerHTML = rows.join('');
      this._bindRowEvents();
      this._activeIndex = -1;
    }

    /** Attach click / mousedown handlers to freshly rendered rows. */
    _bindRowEvents() {
      /* Fresh rows: give each an id for aria-activedescendant, drop any stale reference */
      const idBase = (this.typeahead.id || 'hcf-typeahead') + '-opt-';
      this.searchInput.removeAttribute('aria-activedescendant');
      this.typeahead.querySelectorAll('.hcf__typeahead-item').forEach((el, i) => {
        el.id = idBase + i;
        el.addEventListener('click', () => this._activateRow(el));
        /*
         * Prevent the document-click handler from closing the list before
         * the click event fires on the item itself.
         */
        el.addEventListener('mousedown', e => e.preventDefault());
      });
    }

    /** Navigate to a row's URL, or apply a local model filter (brand pages). */
    _activateRow(el) {
      /* Remember the pick so it can be offered as a recent search */
      const label = el.dataset.label || '';
      if (label) {
        this._saveRecent(label, el.dataset.localModel ? '' : decodeURI(el.dataset.url || ''));
      }
      /* Recent-search row with no URL: re-run the stored query */
      if (el.dataset.fill) {
        this.searchInput.value = el.dataset.fill;
        this.searchInput.focus();
        this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      const localModel = el.dataset.localModel;
      if (localModel) {
        this._applyLocalModel(localModel);
        return;
      }
      const url = decodeURI(el.dataset.url || '');
      if (url) window.location.href = url;
    }

    /** Brand page: click the chip matching `model` (normalised comparison). */
    _applyLocalModel(model) {
      const key = compact(model);
      const chips = this.section.querySelectorAll('[data-hcf-model-filter]');
      for (const chip of chips) {
        if (compact(chip.dataset.hcfModelFilter) === key) {
          if (!chip.classList.contains('is-active')) chip.click();
          this._closeTypeahead();
          this.searchInput.value = chip.dataset.hcfModelFilter;
          this._syncClearBtn(true);
          chip.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          return;
        }
      }
      this._closeTypeahead();
    }

    /** Render the empty-state placeholder + report the miss to analytics. */
    _renderNoResults(contactUrl, query) {
      this.searchInput.removeAttribute('aria-activedescendant');
      this.typeahead.innerHTML = `<div role="option" aria-disabled="true" class="hcf__no-results-item"><p class="hcf__no-results">${this.str.noResults} <a href="${escapeHtml(contactUrl)}" class="hcf__no-results-link">${this.str.getInTouch}</a>.</p></div>`;
      this._activeIndex = -1;
      if (query) this._trackNoResults(query);
    }

    /* -----------------------------------------------------------------------
       Recent searches (localStorage) + zero-result analytics
       -------------------------------------------------------------------- */

    /** @returns {Array<{label: string, url: string}>} */
    _getRecent() {
      try {
        const list = JSON.parse(window.localStorage.getItem('hcf_recent_searches') || '[]');
        return Array.isArray(list) ? list.filter(r => r && r.label) : [];
      } catch (e) {
        return []; /* private browsing / storage disabled */
      }
    }

    /** Store a picked suggestion (most recent first, max 3, deduped). */
    _saveRecent(label, url) {
      if (!label) return;
      try {
        let list = this._getRecent().filter(r => r.label !== label);
        list.unshift({ label: label, url: url || '' });
        window.localStorage.setItem('hcf_recent_searches', JSON.stringify(list.slice(0, 3)));
      } catch (e) { /* ignore */ }
    }

    /** Show the visitor's recent searches when the empty input gains focus. */
    _renderRecent() {
      const list = this._getRecent();
      if (!list.length) return;
      const rows = [`<div class="hcf__typeahead-group" aria-hidden="true">${this.str.recent}</div>`];
      for (const r of list) {
        rows.push(`
          <div
            class="hcf__typeahead-item"
            role="option"
            aria-selected="false"
            ${r.url ? `data-url="${escapeHtml(r.url)}"` : `data-fill="${escapeHtml(r.label)}"`}
            data-label="${escapeHtml(r.label)}"
            tabindex="-1"
          >
            <span>${escapeHtml(r.label)}</span>
          </div>
        `);
      }
      this.typeahead.innerHTML = rows.join('');
      this._bindRowEvents();
      this._activeIndex = -1;
      this._showTypeahead();
    }

    /**
     * Report a zero-result query to Google Analytics (GA4) as a
     * 'search_no_results' event — a running list of headphones customers
     * wanted but couldn't find. Fires once per query per page view.
     * Works with Shopify's Google & YouTube channel (gtag) and with GTM
     * (dataLayer); silently does nothing if neither is present.
     */
    _trackNoResults(query) {
      const key = query.toLowerCase();
      this._trackedNoResults = this._trackedNoResults || new Set();
      if (this._trackedNoResults.has(key)) return;
      this._trackedNoResults.add(key);
      const payload = { search_term: query, finder: 'cable_finder' };
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'search_no_results', payload);
        } else if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push(Object.assign({ event: 'search_no_results' }, payload));
        }
      } catch (e) { /* analytics must never break the finder */ }
    }

    _showTypeahead() {
      if (!this.typeahead) return;
      this.typeahead.classList.add('is-open');
      this.typeahead.removeAttribute('hidden');
      this.typeahead.setAttribute('aria-hidden', 'false');
      this.searchInput.setAttribute('aria-expanded', 'true');
    }

    _closeTypeahead() {
      if (!this.typeahead) return;
      this.typeahead.classList.remove('is-open');
      this.typeahead.setAttribute('aria-hidden', 'true');
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.searchInput.removeAttribute('aria-activedescendant');
      this._activeIndex = -1;
    }

    /* -----------------------------------------------------------------------
       Keyboard navigation (ArrowUp / ArrowDown / Enter / Escape)
       -------------------------------------------------------------------- */

    _handleKeydown(e) {
      const items = /** @type {NodeListOf<HTMLElement>} */ (
        this.typeahead.querySelectorAll('.hcf__typeahead-item')
      );
      const count = items.length;
      const isOpen = this.typeahead.classList.contains('is-open');

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) break;
          this._activeIndex = Math.min(this._activeIndex + 1, count - 1);
          this._syncActiveItem(items);
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) break;
          this._activeIndex = Math.max(this._activeIndex - 1, -1);
          this._syncActiveItem(items);
          break;

        case 'Enter':
          /* Enter activates the highlighted row, or the FIRST row when
             nothing is highlighted — one keystroke to the best match.
             Falls through to a normal search submit when no rows exist. */
          if (this._activeIndex >= 0 && items[this._activeIndex]) {
            e.preventDefault();
            this._activateRow(items[this._activeIndex]);
          } else if (count > 0 && this.typeahead.classList.contains('is-open')) {
            e.preventDefault();
            this._activateRow(items[0]);
          } else if (this._modelsState === 'loading' && this.searchInput.value.trim().length >= 2) {
            /* First visit, index not here yet: wait for it so a model-only query
               ("K271") still lands on its brand page, as with the inline index */
            e.preventDefault();
            this._modelsPromise.then(() => {
              this._refreshAfterModels(true);
              const first = this.typeahead.querySelector('.hcf__typeahead-item');
              const form = this.searchInput.form;
              if (first) this._activateRow(first);
              else if (form) (form.requestSubmit ? form.requestSubmit() : form.submit());
            });
          }
          break;

        case 'Escape':
          this._closeTypeahead();
          this.searchInput.blur();
          break;
      }
    }

    /**
     * Sync the .is-active class and scroll the highlighted item into view.
     * @param {NodeListOf<HTMLElement>} items
     */
    _syncActiveItem(items) {
      items.forEach((el, i) => {
        const active = i === this._activeIndex;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-selected', String(active));
        if (active) el.scrollIntoView({ block: 'nearest' });
      });
      const activeEl = items[this._activeIndex];
      if (activeEl && activeEl.id) this.searchInput.setAttribute('aria-activedescendant', activeEl.id);
      else this.searchInput.removeAttribute('aria-activedescendant');
    }

    /* -----------------------------------------------------------------------
       Clear button
       -------------------------------------------------------------------- */

    _handleClear() {
      this.searchInput.value = '';
      this.searchInput.focus();
      this._syncClearBtn(false);
      this._cancelSuggestions();
      this._closeTypeahead();
    }

    /* Drop a pending debounce / in-flight fetch so a closed typeahead can't reopen with stale results */
    _cancelSuggestions() {
      clearTimeout(this._debounceTimer);
      if (this._abortController) this._abortController.abort();
    }

    _syncClearBtn(visible) {
      if (!this.searchClear) return;
      this.searchClear.classList.toggle('is-visible', visible);
    }

    /* -----------------------------------------------------------------------
       Close typeahead on outside click
       -------------------------------------------------------------------- */

    _handleDocumentClick(e) {
      if (
        this.typeahead &&
        !this.typeahead.contains(/** @type {Node} */ (e.target)) &&
        !this.searchInput.contains(/** @type {Node} */ (e.target))
      ) {
        this._closeTypeahead();
      }
    }

    /* -----------------------------------------------------------------------
       Model chip filtering (brand pages)
       -------------------------------------------------------------------- */

    /**
     * Reads the handle→models JSON from the section's data attribute, then
     * attaches click listeners to every [data-hcf-model-filter] button.
     * On click, toggles `.is-active` on the chip and shows/hides product
     * cards in the Shopify collection grid based on model compatibility.
     * Model comparison is normalised (case/space/hyphen-insensitive) so a
     * chip matches every spelling variant present in the metafields.
     */
    _initModelFilter() {
      /* Parse the handle→models map injected by Liquid */
      let productModels = {};
      try {
        const raw = this.section.dataset.productModels || '{}';
        productModels = JSON.parse(raw);
      } catch (e) {
        console.warn('[HCF] Could not parse data-product-models JSON:', e);
        return;
      }
      this.productModels = productModels;

      /* Pre-compact every product's model list once */
      const compactModels = {};
      for (const [handle, models] of Object.entries(productModels)) {
        compactModels[handle] = models.map(compact);
      }

      /* Collect all model-filter chips */
      const modelChips = /** @type {NodeListOf<HTMLElement>} */ (
        this.section.querySelectorAll('[data-hcf-model-filter]')
      );
      if (!modelChips.length) return;

      /**
       * Find all product cards in the Shopify collection grid.
       * Prefers Dawn's #ProductGridContainer / li.grid__item structure and
       * falls back to a generic heuristic, excluding header/footer/nav and
       * this section itself.
       * @returns {HTMLElement[]}
       */
      const getProductCards = () => {
        const grid = document.querySelector('#ProductGridContainer');
        if (grid) {
          const items = grid.querySelectorAll('li.grid__item');
          if (items.length) return Array.from(items);
        }
        const scope = document.querySelector('main') || document;
        const candidates = /** @type {NodeListOf<HTMLElement>} */ (
          scope.querySelectorAll('li, article')
        );
        return Array.from(candidates).filter(el => {
          if (el.closest('header, footer, nav, .hcf')) return false;
          const link = el.querySelector('a[href*="/products/"]');
          return link !== null;
        });
      };

      /**
       * Extract the product handle from a card element.
       * Looks for the first <a href="/products/{handle}"> link.
       * @param {HTMLElement} card
       * @returns {string}
       */
      const getCardHandle = (card) => {
        const link = card.querySelector('a[href*="/products/"]');
        if (!link) return '';
        const href = link.getAttribute('href') || '';
        const match = href.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      };

      /**
       * Apply or clear the filter.
       * @param {string|null} activeModel  — null means "show all"
       */
      const applyFilter = (activeModel) => {
        const key = activeModel ? compact(activeModel) : null;
        const cards = getProductCards();
        cards.forEach(card => {
          if (!key) {
            card.classList.remove('hcf-product-hidden');
            return;
          }
          const handle = getCardHandle(card);
          const models = compactModels[handle] || [];
          const visible = models.includes(key);
          card.classList.toggle('hcf-product-hidden', !visible);
        });

        /* Keep the grid's "Showing X-Y of Z products" line in step with the
           filter. Only a single-page grid carries data-filtered-template. */
        const countEl = document.querySelector(
          '#ProductGridContainer .collection-product-count__text[data-filtered-template]'
        );
        if (countEl) {
          if (countEl.dataset.hcfOriginal === undefined) {
            countEl.dataset.hcfOriginal = countEl.textContent.trim();
          }
          const shown = key
            ? cards.filter(c => !c.classList.contains('hcf-product-hidden')).length
            : 0;
          countEl.textContent = shown > 0
            ? countEl.dataset.filteredTemplate.split('[n]').join(String(shown))
            : countEl.dataset.hcfOriginal;
        }
      };

      /** Keep ?model= in the URL so filtered views are shareable. */
      const syncUrl = (model) => {
        try {
          const url = new URL(window.location.href);
          if (model) url.searchParams.set('model', model);
          else url.searchParams.delete('model');
          window.history.replaceState({}, '', url);
        } catch (e) { /* ignore */ }
      };

      /* Attach click listener to each model chip */
      modelChips.forEach(chip => {
        chip.addEventListener('click', () => {
          const model = chip.dataset.hcfModelFilter || '';
          const isAlreadyActive = chip.classList.contains('is-active');

          /* Deselect all chips first */
          modelChips.forEach(c => {
            c.classList.remove('is-active');
            c.setAttribute('aria-pressed', 'false');
          });

          if (isAlreadyActive) {
            /* Toggle off — show all products */
            applyFilter(null);
            syncUrl(null);
          } else {
            /* Activate this chip — filter the grid */
            chip.classList.add('is-active');
            chip.setAttribute('aria-pressed', 'true');
            applyFilter(model);
            syncUrl(model);
          }
        });
      });

      /* Deep link: /collections/<brand>?model=<model> auto-applies the filter
         (typeahead model suggestions land here from any page). The script can
         run before the product grid below is parsed, so wait for the document. */
      const applyDeepLink = () => {
        try {
          const wanted = new URLSearchParams(window.location.search).get('model');
          if (wanted) {
            const key = compact(wanted);
            for (const chip of modelChips) {
              if (compact(chip.dataset.hcfModelFilter) === key) {
                chip.classList.add('is-active');
                chip.setAttribute('aria-pressed', 'true');
                applyFilter(chip.dataset.hcfModelFilter);
                break;
              }
            }
          }
        } catch (e) { /* ignore */ }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyDeepLink, { once: true });
      } else {
        applyDeepLink();
      }
    }

    /* -----------------------------------------------------------------------
       Brand chips
       -------------------------------------------------------------------- */

    /** @param {MouseEvent} e */
    _handleChipClick(e) {
      /* On brand pages, model chips are handled by _initModelFilter() — skip navigation */
      if (this.isBrandPage) return;

      /* Ctrl/Cmd/Shift/Alt-click or a non-primary button: let the browser open a new tab/window */
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

      const chip = /** @type {HTMLElement} */ (e.currentTarget);
      const url  = chip.dataset.url || chip.getAttribute('href');

      /* Mark this chip as active, clear the others */
      this.chips.forEach(c => c.classList.toggle('is-active', c === chip));

      /* Anchors already navigate via href; only a non-link chip needs JS navigation */
      if (url && chip.tagName !== 'A') window.location.href = url;
    }

    /* -----------------------------------------------------------------------
       Modal open / close
       -------------------------------------------------------------------- */

    _openModal() {
      if (!this.modal) return;
      this._prevFocus = /** @type {HTMLElement} */ (document.activeElement);

      this.modal.removeAttribute('hidden');
      this.modal.classList.remove('is-closing');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
      });
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      document.addEventListener('keydown', this._onModalKey);

      /* Defer focus trap so the CSS open transition has started */
      requestAnimationFrame(() => {
        this._trapCleanup = trapFocus(this.modalInner);
      });
    }

    _closeModal() {
      if (!this.modal) return;

      const onEnd = () => {
        if (this.modal.classList.contains('is-open')) return; /* reopened meanwhile */
        this.modal.setAttribute('hidden', '');
        this.modal.classList.remove('is-closing');
      };
      /* transitionend never fires when transitions are disabled (reduced motion) */
      setTimeout(onEnd, 600);
      this.modal.classList.remove('is-open');
      this.modal.classList.add('is-closing');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.modal.addEventListener('transitionend', onEnd, { once: true });

      document.removeEventListener('keydown', this._onModalKey);

      if (this._trapCleanup) {
        this._trapCleanup();
        this._trapCleanup = null;
      }

      /* Return focus to the element that opened the modal */
      if (this._prevFocus) {
        this._prevFocus.focus();
        this._prevFocus = null;
      }
    }

    /** @param {KeyboardEvent} e */
    _handleModalKeydown(e) {
      if (e.key === 'Escape') this._closeModal();
    }

    /**
     * Close only when clicking the dark overlay backdrop,
     * not when clicking inside the white panel.
     * @param {MouseEvent} e
     */
    _handleOverlayClick(e) {
      if (e.target === this.modal) this._closeModal();
    }
  }

  /* -------------------------------------------------------------------------
     Shopify Section Lifecycle Registration
     ---------------------------------------------------------------------- */

  /* Shared across executions: every copy of the section emits this <script>
     tag, and the theme editor can run it again, so the registry lives on window */
  /** @type {Map<string, HeadphoneCableFinder>} */
  const instances = window.__hcfInstances || (window.__hcfInstances = new Map());

  /**
   * Mount a HCF instance for the given Shopify section container.
   * @param {HTMLElement|null} container
   */
  function mount(container) {
    if (!container) return;
    const root = container.classList.contains('hcf')
      ? container
      : container.querySelector('.hcf');
    if (!root) return;

    const id = container.dataset.sectionId || root.id || Math.random().toString(36).slice(2);

    /* Avoid double-mounting: a root that already has a live instance is left
       alone (destroy() does not unbind the chip and modal-button listeners);
       an instance whose root was replaced (theme editor) is torn down first */
    const existing = instances.get(id);
    if (existing && existing.section === root) return;
    if (existing) existing.destroy();
    instances.set(id, new HeadphoneCableFinder(root));
  }

  /**
   * Tear down and remove the HCF instance for the given section container.
   * @param {HTMLElement} container
   */
  function unmount(container) {
    const id = container.dataset.sectionId;
    if (id && instances.has(id)) {
      instances.get(id).destroy();
      instances.delete(id);
    }
  }

  if (!window.__hcfEditorWired) {
    window.__hcfEditorWired = true;

    /* Shopify theme editor events */
    document.addEventListener('shopify:section:load', (e) => {
      const sectionId = /** @type {any} */ (e).detail && /** @type {any} */ (e).detail.sectionId;
      mount(
        sectionId
          ? document.getElementById(`shopify-section-${sectionId}`)
          : /** @type {HTMLElement} */ (e.target)
      );
    });

    document.addEventListener('shopify:section:unload', (e) => {
      unmount(/** @type {HTMLElement} */ (e.target));
    });
  }

  /* Initial page load: mount every .hcf on the page. Each section copy's async
     tag runs after its own markup, so the last run sees every root, and mount()
     skips roots that are already bound. */
  document.querySelectorAll('.hcf').forEach((root) => mount(/** @type {HTMLElement} */ (root)));

})(); /* end IIFE */
